import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Calculator } from 'lucide-react';
import FileDrop from '../components/FileDrop';
import PenaltyResults from '../components/PenaltyResults';
import Button from '../components/ui/Button';
import { calcPenaltyFile } from '../api/penalty';
import type { PenaltyResponse } from '../types';

export default function PenaltyPage() {
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<PenaltyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const canSubmit = !!file || text.trim().length > 0;

  useEffect(() => {
    if (!loading) { setElapsed(0); return; }
    const t = setInterval(() => setElapsed((p) => p + 1), 1000);
    return () => clearInterval(t);
  }, [loading]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const handleCalc = async () => {
    if (!canSubmit) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const res = await calcPenaltyFile({ file, text }, controller.signal);
      setResults(res.data);
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      setError(err?.response?.data?.detail || err?.message || 'Произошла ошибка');
    } finally {
      setLoading(false);
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Calculator className="w-5 h-5 text-rose-500" />
          <h2 className="text-lg font-semibold text-gray-800">Расчёт неустойки (ст. 395 ГК РФ)</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4 text-center">
          Загрузите документ или вставьте текст с денежным долгом — рассчитаю проценты по ключевой ставке ЦБ РФ на текущую дату.
        </p>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 max-w-2xl mx-auto">
          <FileDrop file={file} onFileChange={setFile} onError={setError} disabled={loading || text.trim().length > 0} />

          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-600">Или вставьте текст:</label>
              {text && (
                <button onClick={() => setText('')} className="text-xs text-red-500 hover:text-red-700">Очистить</button>
              )}
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={loading || !!file}
              placeholder="Вставьте текст документа..."
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 outline-none transition disabled:bg-gray-100 disabled:text-gray-400"
            />
          </div>
        </div>
      </div>

      <div className="mb-8 text-center">
        <Button onClick={handleCalc} disabled={loading || !canSubmit} loading={loading} className="px-8 py-3">
          {loading ? `Считаю... ${elapsed}с` : 'Рассчитать неустойку'}
        </Button>
        {!canSubmit && !loading && (
          <p className="mt-2 text-sm text-amber-600">Загрузите файл или вставьте текст документа</p>
        )}
      </div>

      {loading && (
        <div className="mb-8 flex justify-center">
          <img
            src="/masha_reading_contracts.gif"
            alt="Маша изучает документ..."
            style={{ maxWidth: '320px', width: '100%', borderRadius: '16px' }}
          />
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-800">Ошибка</h3>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {results && <PenaltyResults results={results} />}
    </>
  );
}
